import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Cell, Table, CellState, TablePermission, TableAccessConfig } from '../../services/tables/tablesService';
import { tablesService } from '../../services/tables/tablesService';

interface TableState {
  cells: { [key: string]: Cell };
  table: Table | null;
  tableName: string;
  tableDescription: string;
  isLoading: boolean;
  isInitialLoad: boolean;
  saveState: 'idle' | 'saving' | 'saved';
  error: string | null;
  lastDocId: string | null;
  hasMore: boolean;
  loadingViewports: { [key: string]: boolean };
}

const initialState: TableState = {
  cells: {},
  table: null,
  tableName: '',
  tableDescription: '',
  isLoading: false,
  isInitialLoad: true,
  saveState: 'idle',
  error: null,
  lastDocId: null,
  hasMore: true,
  loadingViewports: {},
};

const getCellKey = (rowIndex: number, columnIndex: number) => `${rowIndex}_${columnIndex}`;
const getViewportKey = (startRow: number, endRow: number, startCol: number, endCol: number) => 
  `${startRow}-${endRow}-${startCol}-${endCol}`;

// Async thunks
export const fetchTableData = createAsyncThunk(
  'table/fetchTableData',
  async ({ userId, tableId }: { userId: string; tableId: string }) => {
    const table = await tablesService.getTableWithPermissions(userId, tableId);
    if (!table) {
      throw new Error('Table not found');
    }
    return table;
  }
);

export const fetchCellsBatch = createAsyncThunk<
  { cells: Cell[]; lastDocId: string | null; hasMore: boolean },
  { userId: string; tableId: string; lastDocId: string | null }
>(
  'table/fetchCellsBatch',
  async ({ userId, tableId, lastDocId }) => {
    console.log(`[TableSlice] Fetching cells batch, lastDocId: ${lastDocId || 'initial'}`);
    const result = await tablesService.getCellsBatch(userId, tableId, lastDocId);
    console.log(`[TableSlice] Fetched ${result.cells.length} cells in batch`);
    return result;
  }
);

export const fetchCellsByViewport = createAsyncThunk<
  { cells: Cell[]; viewport: { startRow: number; endRow: number; startCol: number; endCol: number } },
  { userId: string; tableId: string; startRow: number; endRow: number; startCol: number; endCol: number; forceLoad?: boolean }
>(
  'table/fetchCellsByViewport',
  async ({ userId, tableId, startRow, endRow, startCol, endCol, forceLoad }, { getState }) => {
    // Get the current state
    const state = getState() as { table: TableState };
    
    // Check if we're already loading this viewport and not forcing a load
    const viewportKey = getViewportKey(startRow, endRow, startCol, endCol);
    if (!forceLoad && state.table.loadingViewports[viewportKey]) {
      return { cells: [], viewport: { startRow, endRow, startCol, endCol } };
    }
    
    console.log(`[TableSlice] Fetching cells for viewport: ${startRow}-${endRow}, ${startCol}-${endCol}`);
    const cells = await tablesService.getCellsByViewport(userId, tableId, startRow, endRow, startCol, endCol);
    console.log(`[TableSlice] Fetched ${cells.length} cells for viewport`);
    return { cells, viewport: { startRow, endRow, startCol, endCol } };
  }
);

interface UpdateCellPayload {
  userId: string;
  tableId: string;
  rowIndex: number;
  columnIndex: number;
  value: string;
  proof?: string;
}

interface UpdateCellResult {
  cellKey?: string;
  deleted: boolean;
  cell?: Cell;
}

export const updateCellValue = createAsyncThunk<UpdateCellResult, UpdateCellPayload>(
  'table/updateCellValue',
  async ({ userId, tableId, rowIndex, columnIndex, value, proof }) => {
    // Check if the value is empty (after trimming whitespace)
    const trimmedValue = value.trim();
    
    // Always call the service, which will handle empty values by deleting the cell
    const updatedCell = await tablesService.updateCell(userId, tableId, rowIndex, columnIndex, trimmedValue, proof);
    
    // If the value was empty, mark as deleted for the reducer
    if (trimmedValue === '') {
      const cellKey = getCellKey(rowIndex, columnIndex);
      return { cellKey, deleted: true };
    }
    
    return { cell: updatedCell, deleted: false };
  }
);

export const updateTableName = createAsyncThunk(
  'table/updateTableName',
  async ({ userId, tableId, newName }: { userId: string; tableId: string; newName: string }) => {
    await tablesService.updateTableName(userId, tableId, newName);
    return { newName };
  }
);

export const updateTableDescription = createAsyncThunk(
  'table/updateTableDescription',
  async ({ userId, tableId, newDescription }: { userId: string; tableId: string; newDescription: string }) => {
    await tablesService.updateTableDescription(userId, tableId, newDescription);
    return { newDescription };
  }
);

export const updateSharing = createAsyncThunk(
  'table/updateSharing',
  async ({ userId, tableId, updates }: { 
    userId: string; 
    tableId: string; 
    updates: Partial<{ permissions: TablePermission[]; access: TableAccessConfig }>
  }) => {
    await tablesService.updateSharing(userId, tableId, updates);
    return { updates };
  }
);

const tableSlice = createSlice({
  name: 'table',
  initialState,
  reducers: {
    resetTableState: (state) => {
      return initialState;
    },
    setSaveState: (state, action: PayloadAction<'idle' | 'saving' | 'saved'>) => {
      state.saveState = action.payload;
    },
    setLocalCellValue: (state, action: PayloadAction<{
      rowIndex: number;
      columnIndex: number;
      value: string;
      proof?: string;
    }>) => {
      const { rowIndex, columnIndex, value, proof } = action.payload;
      const cellKey = getCellKey(rowIndex, columnIndex);
      
      // Update the cell in the local state without writing to Firestore
      if (value === '') {
        // If value is empty, remove the cell from the state
        if (state.cells[cellKey]) {
          delete state.cells[cellKey];
        }
      } else {
        // Otherwise, update or create the cell
        state.cells[cellKey] = {
          ...(state.cells[cellKey] || {}),
          value,
          proof: proof ? { url: proof, timestamp: new Date().toISOString() } : undefined,
          rowIndex,
          columnIndex,
          state: CellState.HAS_VALUE,
          updatedAt: new Date().toISOString()
        };
      }
    },
    updateCellsFromRealtime: (state, action: PayloadAction<{
      updatedCells: Cell[];
      removedCellKeys: string[];
    }>) => {
      // Ensure we have valid arrays even if the payload is malformed
      const updatedCells = action.payload?.updatedCells || [];
      const removedCellKeys = action.payload?.removedCellKeys || [];
      
      // Update cells from real-time updates
      updatedCells.forEach(cell => {
        if (cell && typeof cell.rowIndex === 'number' && typeof cell.columnIndex === 'number') {
          const cellKey = getCellKey(cell.rowIndex, cell.columnIndex);
          
          // If the cell has an empty value and is in EMPTY state, remove it instead of adding it
          if (cell.value === '' && cell.state === CellState.EMPTY) {
            if (state.cells[cellKey]) {
              delete state.cells[cellKey];
            }
          } else {
            state.cells[cellKey] = cell;
          }
        }
      });
      
      // Remove deleted cells
      removedCellKeys.forEach(cellKey => {
        if (cellKey && state.cells[cellKey]) {
          delete state.cells[cellKey];
        }
      });
    },
    updateTableFromRealtime: (state, action: PayloadAction<Table>) => {
      // Update table metadata from real-time updates
      const updatedTable = action.payload;
      state.table = updatedTable;
      state.tableName = updatedTable.name;
      state.tableDescription = updatedTable.description || '';
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchTableData
      .addCase(fetchTableData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTableData.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.table = action.payload;
          state.tableName = action.payload.name;
          state.tableDescription = action.payload.description || '';
        }
      })
      .addCase(fetchTableData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch table data';
      })
      // Handle fetchCellsBatch
      .addCase(fetchCellsBatch.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCellsBatch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialLoad = false;
        const { cells, lastDocId, hasMore } = action.payload;
        
        cells.forEach((cell: Cell) => {
          const key = getCellKey(cell.rowIndex, cell.columnIndex);
          state.cells[key] = cell;
        });
        
        state.lastDocId = lastDocId;
        state.hasMore = hasMore;
      })
      .addCase(fetchCellsBatch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch cells';
      })
      // Handle fetchCellsByViewport
      .addCase(fetchCellsByViewport.pending, (state, action) => {
        const { startRow, endRow, startCol, endCol } = action.meta.arg;
        const viewportKey = getViewportKey(startRow, endRow, startCol, endCol);
        state.loadingViewports[viewportKey] = true;
      })
      .addCase(fetchCellsByViewport.fulfilled, (state, action) => {
        const { cells, viewport } = action.payload;
        const { startRow, endRow, startCol, endCol } = viewport;
        const viewportKey = getViewportKey(startRow, endRow, startCol, endCol);
        
        // Add cells to state
        cells.forEach((cell: Cell) => {
          const key = getCellKey(cell.rowIndex, cell.columnIndex);
          state.cells[key] = cell;
        });
        
        // Mark viewport as loaded
        delete state.loadingViewports[viewportKey];
      })
      .addCase(fetchCellsByViewport.rejected, (state, action) => {
        const { startRow, endRow, startCol, endCol } = action.meta.arg;
        const viewportKey = getViewportKey(startRow, endRow, startCol, endCol);
        delete state.loadingViewports[viewportKey];
        state.error = action.error.message || 'Failed to fetch cells by viewport';
      })
      // Handle updateCellValue
      .addCase(updateCellValue.pending, (state) => {
        state.saveState = 'saving';
      })
      .addCase(updateCellValue.fulfilled, (state, action) => {
        state.saveState = 'saved';
        if (action.payload.deleted && action.payload.cellKey) {
          delete state.cells[action.payload.cellKey];
        } else if (action.payload.cell) {
          const cell = action.payload.cell;
          const key = getCellKey(cell.rowIndex, cell.columnIndex);
          state.cells[key] = cell;
        }
      })
      .addCase(updateCellValue.rejected, (state, action) => {
        state.saveState = 'idle';
        state.error = action.error.message || 'Failed to update cell';
      })
      // Handle updateTableName
      .addCase(updateTableName.pending, (state) => {
        state.saveState = 'saving';
      })
      .addCase(updateTableName.fulfilled, (state, action) => {
        state.saveState = 'saved';
        state.tableName = action.payload.newName;
      })
      .addCase(updateTableName.rejected, (state, action) => {
        state.saveState = 'idle';
        state.error = action.error.message || 'Failed to update table name';
      })
      // Handle updateTableDescription
      .addCase(updateTableDescription.pending, (state) => {
        state.saveState = 'saving';
      })
      .addCase(updateTableDescription.fulfilled, (state, action) => {
        state.saveState = 'saved';
        state.tableDescription = action.payload.newDescription;
        // Also update the table object description to ensure UI updates immediately
        if (state.table) {
          state.table.description = action.payload.newDescription;
        }
      })
      .addCase(updateTableDescription.rejected, (state, action) => {
        state.saveState = 'idle';
        state.error = action.error.message || 'Failed to update table description';
      })
      // Handle updateSharing
      .addCase(updateSharing.pending, (state) => {
        state.saveState = 'saving';
      })
      .addCase(updateSharing.fulfilled, (state, action) => {
        state.saveState = 'saved';
        // Update table permissions and access in state
        if (state.table) {
          if (action.payload.updates.permissions) {
            state.table.permissions = action.payload.updates.permissions;
          }
          if (action.payload.updates.access) {
            state.table.access = action.payload.updates.access;
          }
        }
      })
      .addCase(updateSharing.rejected, (state, action) => {
        state.saveState = 'idle';
        state.error = action.error.message || 'Failed to update sharing settings';
      });
  },
});

export const { resetTableState, setSaveState, setLocalCellValue, updateCellsFromRealtime, updateTableFromRealtime } = tableSlice.actions;
export default tableSlice.reducer; 